import { Button, DatePicker, Form, Input, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  DefaultDateTime,
  DefaultGUID,
  VALIDATION_MESSAGE_CONFIG,
} from '../../../../constants/app-constants';
import { ApiManagement } from '../../../../models/api-management';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import DateUtils from '../../../../utils/date.utils';
import { ApiManagementApi } from '../../../../apis/api-management.api';
import showNotification from '../../../common/notification';
import InsertPasscodeForm from '../../../common/modal-mfa/modal-input-passcode';
import './modal.scss';
import { CopyOutlined } from '@ant-design/icons';

interface ModalApiManagementProps {
  visible: boolean;
  onClose: () => void;
  data: ApiManagement | undefined;
}

function ModalApiManagement(props: ModalApiManagementProps) {
  const { t } = useTranslation();
  const [accesscodeForm] = Form.useForm();
  const [isModalVerifyOpen, setIsModalVerifyOpen] = useState(false);
  const [visibleAppsercet, setVisibleAppSercet] = useState<boolean>(false);
  const [isLoadingModal, setLoadingModal] = useState(false);
  const [title, setTitle] = useState<string>('');
  const [isUpdate, setIsUpdate] = useState<boolean>(false);

  useEffect(() => {
    checkTitle();
  }, [props.data]);

  const handleCancel = () => {
    setVisibleAppSercet(false);
    props.onClose();
  };

  const handleOk = () => {
    setVisibleAppSercet(false);
    accesscodeForm.submit();
  };

  const { RangePicker } = DatePicker;

  async function handleUpdateApiManagement(values: any) {
    setLoadingModal(true);
    try {
      var datePicker: string[] = values.Picker ?? [];
      const model: ApiManagement = {
        id: props.data?.id ?? DefaultGUID,
        name: values.name,
        fromDate: DateUtils.convertDatePickerTimeToUTCTime(datePicker[0]),
        toDate: DateUtils.convertDatePickerTimeToUTCTime(datePicker[1]),
      };

      if (props.data?.id === undefined) {
        await ApiManagementApi.createApiManagement(model);
      }

      showNotification('success', t('api_management.noti.create_success'));
      accesscodeForm.resetFields();
    } catch (error) {
      showNotification('error', t('api_management.noti.error_create'));
    } finally {
      props.onClose();
      setLoadingModal(false);
    }
  }

  function checkTitle() {
    if (props.data === undefined) {
      setIsUpdate(false);
      accesscodeForm.resetFields();
      setTitle(t('api_management.title.create_modal'));
    }
  }

  function verifyToViewAppSecret(visible?: boolean) {
    console.log(visibleAppsercet);
    if (visible) {
    setIsModalVerifyOpen(!isModalVerifyOpen);
    }
    else {
      setVisibleAppSercet(false);
    }
  }

  const visibleAppSercetAfterVerification = async () => {
    setIsModalVerifyOpen(!isModalVerifyOpen);
    try {
      const appSecretKey = await ApiManagementApi.getCurrentAppSercet(props.data?.id ?? DefaultGUID);
      accesscodeForm.setFieldValue("appSecret", appSecretKey.data);
      setVisibleAppSercet(true);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  function handleCopyData(fieldName: string) {
    const data = accesscodeForm.getFieldValue(fieldName);

    navigator.clipboard
      .writeText(data)
      .then(() => {
        showNotification('success', `Copied ${fieldName} to clipboard`);
      })
      .catch(() => {
        showNotification('error', `Failed to copy: ${fieldName}`);
      });
  }

  function handleCopyAppSecret(fieldName: string) {
    if (visibleAppsercet === true) {
      handleCopyData(fieldName);
    } else {
      verifyToViewAppSecret(true);
    }
  }

  return (
    <>
      <Modal
        title={title}
        open={props.visible}
        okText="Save"
        okButtonProps={{ loading: isLoadingModal }}
        onOk={handleOk}
        onCancel={handleCancel}>
        <Form
          layout="vertical"
          validateMessages={VALIDATION_MESSAGE_CONFIG}
          form={accesscodeForm}
          onFinish={handleUpdateApiManagement}
          className="modal-container">
          <Form.Item
            name="name"
            label={t('api_management.title.name')}
            rules={[{ required: true }]}>
            <Input
              className="input-base"
              placeholder={t('api_management.title.name')}
            />
          </Form.Item>
          {isUpdate && (
            <>
              <div className="group-appkey">
                <Form.Item
                  name="appKey"
                  label={t('api_management.title.appKey')}
                  rules={[{ required: true }]}>
                  <Input
                    disabled
                    className="input-base app-key-input"
                    placeholder={t('api_management.title.appKey')}
                  />
                </Form.Item>
                <Button
                  onClick={() => handleCopyData('appKey')}
                  icon={<CopyOutlined />}
                />
              </div>
              <div className="group-appsercet">
                <Form.Item
                  name="appSecret"
                  label={t('api_management.title.appSecret')}
                  rules={[{ required: true }]}>
                  <Input.Password
                    readOnly
                    className="input-base app-secret-input"
                    placeholder={t('api_management.title.appSecret')}
                    visibilityToggle={{
                      visible: visibleAppsercet,
                      onVisibleChange: (visible) => verifyToViewAppSecret(visible),
                    }}
                  />
                </Form.Item>
                <Button
                  onClick={() => handleCopyAppSecret('appSecret')}
                  icon={<CopyOutlined />}
                />
              </div>
            </>
          )}
          <Form.Item
            label={t('api_management.title.activationTime')}
            name="Picker"
            getValueProps={(i) => ({
              value: i
                ? (i as [string, string]).map((dateString) => dayjs(dateString))
                : null,
            })}
            rules={[{ required: true }]}>
            <RangePicker
              format={'YYYY-MM-DD'}
              className="datepicker-input input-base"
            />
          </Form.Item>
        </Form>
      </Modal>
      <InsertPasscodeForm
        visible={isModalVerifyOpen}
        onClose={verifyToViewAppSecret}
        formSubmit={undefined}
        usingcase="check-password"
        onCallBack={visibleAppSercetAfterVerification}
      />
    </>
  );
}

export default ModalApiManagement;
